import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ChevronDown, ChevronRight, ArrowLeft, ArrowRight,
  Clock, CheckCircle, Image as ImageIcon, Video, Menu, X, Home, Maximize2,
} from 'lucide-react';
import {
  TUTORIAL_PRODUCTS,
  getProduct,
  getArticle,
  findArticleById,
  getAdjacentArticles,
  type TutorialProduct,
  type TutorialCategory,
  type TutorialArticle,
  type Difficulty,
} from '@/data/tutorials';

// ─── Difficulty badge ─────────────────────────────────────────────────────────

const DIFFICULTY_STYLES: Record<Difficulty, string> = {
  Easy: 'bg-green-100 text-green-800',
  Medium: 'bg-yellow-100 text-yellow-800',
  Advanced: 'bg-red-100 text-red-800',
};

function DifficultyBadge({ level }: { level: Difficulty }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${DIFFICULTY_STYLES[level]}`}>
      {level}
    </span>
  );
}

// ─── Image / Video placeholders ────────────────────────────────────────────────

function ImagePlaceholder({ label }: { label: string }) {
  return (
    <div className="my-5 flex items-center justify-center rounded-2xl border-2 border-dashed border-brand-yellow bg-brand-yellow/10 p-10">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-yellow/30">
          <ImageIcon className="h-6 w-6 text-brand-black/40" />
        </div>
        <p className="text-sm font-semibold text-brand-black/40">{label}</p>
      </div>
    </div>
  );
}

function VideoPlaceholder() {
  return (
    <div className="mb-8 flex items-center justify-center rounded-2xl border-2 border-dashed border-brand-blue/30 bg-brand-blue/5 p-14">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-brand-blue/10">
          <Video className="h-7 w-7 text-brand-blue/60" />
        </div>
        <p className="text-sm font-bold text-brand-blue/60">Video tutorial coming soon</p>
        <p className="mt-1 text-xs text-brand-black/40">A walkthrough video will be embedded here</p>
      </div>
    </div>
  );
}

function VideoPlayer({ url }: { url: string }) {
  return (
    <div className="mb-8 overflow-hidden rounded-2xl shadow-xl">
      <video
        src={url}
        controls
        playsInline
        className="w-full"
      />
    </div>
  );
}

function SmartImage({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  function onLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    setPortrait(naturalHeight > naturalWidth);
    setLoaded(true);
  }

  return (
    <>
      <div className="group relative mt-4 flex justify-center">
        <img
          src={src}
          alt={alt}
          onLoad={onLoad}
          onClick={() => setZoomed(true)}
          className={[
            'cursor-zoom-in border border-brand-black/10 transition-opacity duration-300',
            loaded ? 'opacity-100' : 'opacity-0',
            portrait
              ? 'max-h-[520px] w-auto max-w-full rounded-3xl shadow-2xl'
              : 'w-full rounded-2xl shadow-md',
          ].join(' ')}
        />
        {loaded && (
          <div className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/40 p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-3 w-3 text-white" />
          </div>
        )}
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setZoomed(false)}
        >
          <button
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={src}
            alt={alt}
            className="max-h-[90vh] max-w-[90vw] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

interface SidebarProps {
  product: TutorialProduct;
  activeCategoryId: string;
  activeArticleId: string;
  onNavigate: (categoryId: string, articleId: string) => void;
}

function Sidebar({ product, activeCategoryId, activeArticleId, onNavigate }: SidebarProps) {
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    Object.fromEntries(product.categories.map((c) => [c.id, c.id === activeCategoryId])),
  );

  function toggleCategory(id: string) {
    setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <nav className="flex flex-col gap-1 py-2">
      {product.categories.map((category) => {
        const isOpen = openCategories[category.id];
        const isCategoryActive = category.id === activeCategoryId;

        return (
          <div key={category.id}>
            <button
              onClick={() => toggleCategory(category.id)}
              className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-colors ${
                isCategoryActive
                  ? 'bg-brand-yellow/30 text-brand-black'
                  : 'text-brand-black/70 hover:bg-brand-black/5'
              }`}
            >
              <span>{category.title}</span>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-60" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-60" />
              )}
            </button>

            {isOpen && (
              <div className="ml-3 mt-1 flex flex-col gap-0.5 border-l-2 border-brand-yellow/40 pl-3">
                {category.articles.map((article) => {
                  const isActive = article.id === activeArticleId;
                  return (
                    <button
                      key={article.id}
                      onClick={() => onNavigate(category.id, article.id)}
                      className={`rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? 'bg-brand-blue text-white font-semibold'
                          : 'text-brand-black/60 hover:bg-brand-black/5 hover:text-brand-black'
                      }`}
                    >
                      {article.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ─── Article body ─────────────────────────────────────────────────────────────

interface ArticleViewProps {
  product: TutorialProduct;
  category: TutorialCategory;
  article: TutorialArticle;
  onNavigate: (categoryId: string, articleId: string) => void;
}

function ArticleView({ product, category, article, onNavigate }: ArticleViewProps) {
  const { prev, next } = getAdjacentArticles(product, category.id, article.id);

  const relatedEntries = (article.relatedArticleIds ?? [])
    .map((id) => findArticleById(product, id))
    .filter(Boolean) as { category: TutorialCategory; article: TutorialArticle }[];

  return (
    <article className="mx-auto max-w-3xl px-2 py-8 md:px-8">
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center gap-2 text-xs font-medium text-brand-black/40">
        <span>{product.title}</span>
        <ChevronRight className="h-3 w-3" />
        <span>{category.title}</span>
      </div>

      {/* Feature intro — shown for every article in this category */}
      {category.intro && (
        <div className="mb-8 rounded-2xl border border-brand-blue/15 bg-brand-blue/5 px-5 py-4">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-brand-blue/60">
            About this feature
          </p>
          <p className="text-sm font-medium text-brand-black/80 leading-relaxed">
            {category.intro}
          </p>
        </div>
      )}

      {/* Header */}
      <h1 className="mb-3 text-3xl font-extrabold text-brand-black leading-tight md:text-4xl">
        {article.title}
      </h1>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <DifficultyBadge level={article.difficulty} />
        <div className="flex items-center gap-1 text-xs font-medium text-brand-black/50">
          <Clock className="h-3.5 w-3.5" />
          <span>{article.estimatedTime} read</span>
        </div>
      </div>

      <p className="mb-8 text-base font-medium text-brand-black/70 leading-relaxed border-l-4 border-brand-yellow pl-4">
        {article.description}
      </p>

      {/* Video slot */}
      {article.hasVideoSlot && (
        article.videoUrl ? <VideoPlayer url={article.videoUrl} /> : <VideoPlaceholder />
      )}

      {/* Steps */}
      <div className="flex flex-col gap-6">
        {article.steps.map((step, index) => (
          <div key={index} className="flex gap-4">
            <div className="flex-shrink-0 mt-0.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-yellow text-xs font-extrabold text-brand-black shadow-sm">
                {index + 1}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-medium text-brand-black leading-relaxed">{step.text}</p>
              {step.imageUrl ? (
                <SmartImage
                  src={step.imageUrl}
                  alt={step.imagePlaceholder ?? `Step ${index + 1}`}
                />
              ) : step.imagePlaceholder ? (
                <ImagePlaceholder label={step.imagePlaceholder} />
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {/* Done indicator */}
      <div className="mt-10 flex items-center gap-2 rounded-2xl bg-green-50 px-5 py-4">
        <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-600" />
        <p className="text-sm font-semibold text-green-800">
          You've completed this guide — {article.title}
        </p>
      </div>

      {/* Related articles */}
      {relatedEntries.length > 0 && (
        <div className="mt-10">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-black/40">
            Related articles
          </h3>
          <div className="flex flex-col gap-2">
            {relatedEntries.map(({ category: relCat, article: relArt }) => (
              <button
                key={relArt.id}
                onClick={() => onNavigate(relCat.id, relArt.id)}
                className="flex items-center justify-between rounded-xl border border-brand-black/10 bg-brand-white px-4 py-3 text-left transition-all hover:border-brand-blue/30 hover:shadow-md"
              >
                <div>
                  <p className="text-sm font-bold text-brand-black">{relArt.title}</p>
                  <p className="text-xs text-brand-black/50">{relCat.title}</p>
                </div>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-brand-black/30" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Prev / Next navigation */}
      <div className="mt-10 grid grid-cols-2 gap-4 border-t border-brand-black/10 pt-8">
        {prev ? (
          <button
            onClick={() => onNavigate(prev.category.id, prev.article.id)}
            className="flex items-start gap-3 rounded-xl border border-brand-black/10 bg-brand-white p-4 text-left transition-all hover:border-brand-blue/30 hover:shadow-md"
          >
            <ArrowLeft className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-black/40" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-brand-black/40">Previous</p>
              <p className="mt-0.5 text-sm font-bold text-brand-black">{prev.article.title}</p>
            </div>
          </button>
        ) : <div />}

        {next ? (
          <button
            onClick={() => onNavigate(next.category.id, next.article.id)}
            className="flex items-start justify-end gap-3 rounded-xl border border-brand-black/10 bg-brand-white p-4 text-right transition-all hover:border-brand-blue/30 hover:shadow-md"
          >
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-brand-black/40">Next</p>
              <p className="mt-0.5 text-sm font-bold text-brand-black">{next.article.title}</p>
            </div>
            <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-black/40" />
          </button>
        ) : <div />}
      </div>
    </article>
  );
}

// ─── Product switcher strip ────────────────────────────────────────────────────

function ProductSwitcher({ activeProductId }: { activeProductId: string }) {
  const navigate = useNavigate();

  return (
    <div className="flex gap-2 border-b border-brand-black/10 bg-brand-white px-4 py-2 overflow-x-auto">
      {TUTORIAL_PRODUCTS.map((product) => {
        const isActive = product.id === activeProductId;
        const firstCat = product.categories[0];
        const firstArt = firstCat?.articles[0];
        return (
          <button
            key={product.id}
            onClick={() => {
              if (firstCat && firstArt) {
                navigate({
                  to: '/tutorials/$productId/$categoryId/$articleId',
                  params: { productId: product.id, categoryId: firstCat.id, articleId: firstArt.id },
                });
              }
            }}
            className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
              isActive
                ? 'bg-brand-yellow text-brand-black'
                : 'text-brand-black/50 hover:bg-brand-black/5'
            }`}
          >
            {product.title}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TutorialViewerPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as {
    productId?: string;
    categoryId?: string;
    articleId?: string;
  };

  const { productId, categoryId, articleId } = params;
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const product = productId ? getProduct(productId) : undefined;
  const article = productId && categoryId && articleId
    ? getArticle(productId, categoryId, articleId)
    : undefined;
  const category = product?.categories.find((c) => c.id === categoryId);

  if (!product || !article || !category) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-yellow">
        <div className="text-center">
          <p className="text-xl font-bold text-brand-black">Guide not found.</p>
          <button
            onClick={() => navigate({ to: '/tutorials' })}
            className="mt-4 rounded-xl bg-brand-blue px-6 py-2 text-sm font-bold text-white"
          >
            Back to Tutorials
          </button>
        </div>
      </div>
    );
  }

  function handleNavigate(newCategoryId: string, newArticleId: string) {
    navigate({
      to: '/tutorials/$productId/$categoryId/$articleId',
      params: { productId: product!.id, categoryId: newCategoryId, articleId: newArticleId },
    });
    setMobileSidebarOpen(false);
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-brand-white shadow-sm">
        {/* Back to tutorials */}
        <div className="flex items-center justify-between border-b border-brand-black/10 px-4 py-2">
          <button
            onClick={() => navigate({ to: '/tutorials' })}
            className="flex items-center gap-1.5 text-xs font-bold text-brand-black/50 hover:text-brand-blue transition-colors"
          >
            <Home className="h-3.5 w-3.5" />
            <span>All Tutorials</span>
          </button>

          {/* Mobile sidebar toggle */}
          <button
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="flex items-center gap-1.5 rounded-lg bg-brand-yellow px-3 py-1.5 text-xs font-bold text-brand-black lg:hidden"
          >
            {mobileSidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            <span>{mobileSidebarOpen ? 'Close' : 'Topics'}</span>
          </button>
        </div>

        {/* Product switcher */}
        <ProductSwitcher activeProductId={product.id} />
      </div>

      {/* Body */}
      <div className="flex flex-1 relative">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-72 flex-shrink-0 flex-col border-r border-brand-black/10 bg-brand-white">
          <div className="p-4 border-b border-brand-black/10">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-black/40">
              {product.title}
            </p>
            <p className="mt-0.5 text-sm font-medium text-brand-black/60">{product.subtitle}</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <Sidebar
              product={product}
              activeCategoryId={categoryId!}
              activeArticleId={articleId!}
              onNavigate={handleNavigate}
            />
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-30 lg:hidden" onClick={() => setMobileSidebarOpen(false)}>
            <div className="absolute inset-0 bg-black/40" />
            <aside
              className="absolute left-0 top-0 bottom-0 w-72 bg-brand-white shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-brand-black/10">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-black/40">
                  {product.title}
                </p>
                <p className="mt-0.5 text-sm font-medium text-brand-black/60">{product.subtitle}</p>
              </div>
              <div className="p-3">
                <Sidebar
                  product={product}
                  activeCategoryId={categoryId!}
                  activeArticleId={articleId!}
                  onNavigate={handleNavigate}
                />
              </div>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-brand-white">
          <ArticleView
            product={product}
            category={category}
            article={article}
            onNavigate={handleNavigate}
          />
        </main>
      </div>
    </div>
  );
}
